import React, { useState, useCallback } from "react";
import TextField from "@bmi-digital/components/text-field";
import { microCopy } from "@bmi-digital/dxb-microcopies";
import { useSiteContext } from "../Site";
import {
  StyledVisibilityOffIcon,
  StyledVisibilityIcon
} from "./styles/PasswordFieldStyles";
import PasswordCriteria from "./PasswordCriteria";
import { FieldNames } from "./types";
import type { PasswordStatus, PasswordFieldProps } from "./types";

const PasswordField = ({ setIsPasswordCorrect }: PasswordFieldProps) => {
  const { getMicroCopy } = useSiteContext();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [passwordStatus, setPasswordStatus] = useState<PasswordStatus>({
    hasCapitalLetter: false,
    hasSpecialCharacters: false,
    isLengthCorrect: false,
    hasLowerCaseLettersOrDigits: false
  });

  const toggleShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const handleChange = useCallback(
    (value: string) => {
      // Validate lowercase letters and digits
      const lowerCaseLettersOrDigits = /[a-z0-9]/g;
      const hasLowerCaseLettersOrDigits = Boolean(
        value.match(lowerCaseLettersOrDigits)
      );

      // Validate capital letters
      const upperCaseLetters = /[A-Z]/g;
      const hasCapitalLetter = Boolean(value.match(upperCaseLetters));

      // Validate special characters
      const specials = /[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/g;
      const hasSpecialCharacters = Boolean(value.match(specials));

      // Validate length
      const isLengthCorrect = value.length >= 6;

      setPasswordStatus({
        hasCapitalLetter,
        hasSpecialCharacters,
        isLengthCorrect,
        hasLowerCaseLettersOrDigits
      });

      setIsPasswordCorrect(
        hasLowerCaseLettersOrDigits &&
          hasCapitalLetter &&
          hasSpecialCharacters &&
          isLengthCorrect
      );
    },
    [setIsPasswordCorrect]
  );

  return (
    <>
      <TextField
        data-testid="embedded-registration-form-password-field"
        name={FieldNames.password}
        label={getMicroCopy(microCopy.REGISTRATION_FORM_PASSWORD_FIELD_LABEL)}
        variant="hybrid"
        type={showPassword ? "text" : "password"}
        isRequired
        fieldIsRequiredError={getMicroCopy(
          microCopy.VALIDATION_ERRORS_FIELD_REQUIRED
        )}
        onChange={handleChange}
        fullWidth
        rightAdornment={
          showPassword ? (
            <StyledVisibilityOffIcon
              onClick={toggleShowPassword}
              data-testid="embedded-registration-form-hide-password-btn"
            />
          ) : (
            <StyledVisibilityIcon
              onClick={toggleShowPassword}
              data-testid="embedded-registration-form-show-password-btn"
            />
          )
        }
      />
      <PasswordCriteria passwordStatus={passwordStatus} />
    </>
  );
};

export default PasswordField;
